import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAdminAnswerFeedback } from "@/lib/api";

import { AnswerFeedbackTable } from "./answer-feedback-table";
import type { SortDir } from "./feedback-table";

const PAGE_SIZE = 25;

type HelpfulFilter = "all" | "helpful" | "unhelpful";

/**
 * Browser for Teams' whole-answer ratings. Like {@link FeedbackExplorer}, all
 * filtering, searching, sorting and pagination is server-side.
 */
export function AnswerFeedbackExplorer() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [helpful, setHelpful] = useState<HelpfulFilter>("all");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [page, setPage] = useState(1);

  // Debounce the search box so we don't query on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const answersQuery = useAdminAnswerFeedback({
    search: debouncedSearch || undefined,
    isHelpful: helpful === "all" ? undefined : helpful === "helpful",
    sortDir,
    page,
    pageSize: PAGE_SIZE,
  });

  const data = answersQuery.data ?? { items: [], total: 0, page: 1, page_size: PAGE_SIZE };
  const loading = answersQuery.isFetching;

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const from = data.total === 0 ? 0 : (data.page - 1) * PAGE_SIZE + 1;
  const to = Math.min(data.page * PAGE_SIZE, data.total);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="text-muted absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input
            placeholder="Search people, comments and part IDs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={helpful}
          onChange={(e) => {
            setHelpful(e.target.value as HelpfulFilter);
            setPage(1);
          }}
          className="border-border bg-background h-9 rounded-md border px-3 text-sm"
        >
          <option value="all">All ratings</option>
          <option value="helpful">Helpful only</option>
          <option value="unhelpful">Not helpful only</option>
        </select>
      </div>

      {answersQuery.isError ? (
        // Without this a failed request falls back to the empty page above and
        // reads as "No Teams answer ratings yet".
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted text-sm">
              Could not load answer ratings. Please{" "}
              <button
                type="button"
                onClick={() => {
                  void answersQuery.refetch();
                }}
                className="underline"
              >
                try again
              </button>
              .
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <AnswerFeedbackTable
            entries={data.items}
            searchTerm={debouncedSearch}
            sortDir={sortDir}
            onToggleSort={() => {
              setSortDir((d) => (d === "asc" ? "desc" : "asc"));
              setPage(1);
            }}
            filtered={Boolean(debouncedSearch) || helpful !== "all"}
            loading={loading}
          />

          <div className="flex items-center justify-between text-sm">
            <span className="text-muted">
              {from}–{to} of {data.total}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={data.page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
                Prev
              </Button>
              <span className="text-muted">
                Page {data.page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={data.page >= totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
